import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { AnimatedSection } from "@/components/site/Motion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { slugifyCatalogName } from "@/lib/catalogDetail";

const THUMB_BASE = "/catalog";

/** Public URL for thumbnails: absolute `/assets/...` or basename under `/catalog/`. */
function resolveThumbnailSrc(thumbnail) {
  const raw = thumbnail?.trim();
  if (!raw) return null;
  if (raw.startsWith("/")) {
    return (
      "/" +
      raw
        .split("/")
        .filter(Boolean)
        .map((seg) => encodeURIComponent(seg))
        .join("/")
    );
  }
  return `${THUMB_BASE}/${encodeURIComponent(raw)}`;
}

function hasLiveDemo(url) {
  return typeof url === "string" && /^https?:\/\//i.test(url.trim());
}

function sortItems(items) {
  return [...items].sort((a, b) => {
    const aLive = hasLiveDemo(a.url);
    const bLive = hasLiveDemo(b.url);
    if (aLive === bLive) return 0;
    return aLive ? -1 : 1;
  });
}

function CatalogCard({ item, index, theme, idleLabel, kind }) {
  const live = hasLiveDemo(item.url);
  const thumbSrc = resolveThumbnailSrc(item.thumbnail);
  const [imgFailed, setImgFailed] = useState(false);
  const useThumb = Boolean(thumbSrc) && !imgFailed;
  const isOrange = theme === "orange";
  const kindLabel = kind === "project" ? "Project" : "Product";

  const hoverBorder = isOrange
    ? "hover:border-primary/40 hover:shadow-lg hover:shadow-primary/15"
    : "hover:border-indigo-200 hover:shadow-lg hover:shadow-indigo-100/60";

  const cardClasses = cn(
    "group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-[0_2px_12px_-4px_rgba(15,23,42,.1)] ring-1 ring-slate-900/[0.04] transition-all duration-300",
    live ? hoverBorder : "hover:border-slate-300",
    live ? "hover:-translate-y-1" : ""
  );

  const badgeCls = isOrange
    ? "border-primary/30 bg-primary/10 text-primary"
    : "border-indigo-200/80 bg-indigo-50/90 text-indigo-800";

  const preview = (
    <div className="relative h-40 shrink-0 bg-slate-100 sm:h-48">
      {live && useThumb && thumbSrc ? (
        <>
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={thumbSrc}
              alt={`${item.name} preview`}
              decoding="async"
              className="h-full w-full object-cover object-top"
              onError={() => setImgFailed(true)}
            />
          </div>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-white/25 via-transparent to-transparent" />
        </>
      ) : live ? (
        <>
          <div className="absolute inset-0 overflow-hidden">
            <iframe
              src={item.url}
              title={item.name}
              loading="lazy"
              className="pointer-events-none border-0"
              style={{
                width: "300%",
                height: "300%",
                transform: "scale(0.3333)",
                transformOrigin: "0 0",
              }}
            />
          </div>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-white/25 via-transparent to-transparent" />
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
          <span className="px-4 text-center text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            {idleLabel}
          </span>
        </div>
      )}
    </div>
  );

  const body = (
    <div className="flex flex-1 flex-col border-t border-slate-100 bg-white p-5 md:p-6">
      <span
        className={cn(
          "inline-flex w-fit rounded-md border px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.14em]",
          badgeCls
        )}
      >
        {kindLabel}
      </span>
      <h3 className="mt-3 text-[1.0625rem] font-bold leading-snug text-slate-900">{item.name}</h3>
      <p className="mt-2 line-clamp-2 text-sm leading-snug text-slate-600">{item.text}</p>
      <div className="mt-auto pt-5">
        {live ? (
          <Button asChild variant="outline" size="sm" className="h-9 w-full border-slate-200 bg-white text-xs font-semibold shadow-sm">
            <a href={item.url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1.5">
              Live
              <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </a>
          </Button>
        ) : (
          <Button type="button" variant="outline" size="sm" disabled className="h-9 w-full text-xs font-semibold">
            Live
          </Button>
        )}
      </div>
    </div>
  );

  const inner = (
    <>
      {preview}
      {body}
    </>
  );

  const delay = index * 60;

  return (
    <AnimatedSection
      as="article"
      delay={delay}
      once
      threshold={0.2}
      className={cn(cardClasses, "h-full")}
      aria-label={item.name}
    >
      {inner}
    </AnimatedSection>
  );
}

export function PortfolioCatalog({
  items,
  theme,
  eyebrow,
  titleLine,
  description,
  idleLabel,
  kind = "product",
}) {
  const ordered = useMemo(() => sortItems(items), [items]);
  const isOrange = theme === "orange";
  const eyebrowCls = isOrange ? "text-primary/90" : "text-indigo-700/90";
  const count = ordered.length;

  return (
    <section className="relative bg-gradient-to-b from-slate-50/90 via-white to-white py-10 md:py-14 min-h-[500px]">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8"
        aria-hidden
      >
        <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-5 border-b border-slate-200/80 pb-6 md:flex-row md:items-end md:justify-between md:gap-8 md:pb-7">
          <AnimatedSection as="div" delay={0} threshold={0.2} className="min-w-0 max-w-2xl text-left">
            <p className={cn("text-[0.7rem] font-semibold uppercase tracking-[0.22em]", eyebrowCls)}>
              {eyebrow}
            </p>
            <h2 className="mt-3 text-2xl font-black leading-[1.1] tracking-tight text-slate-900 md:text-4xl">
              {titleLine(count)}
            </h2>
            {description ? (
              <p className="mt-4 text-pretty text-sm leading-relaxed text-slate-600 md:text-base">{description}</p>
            ) : null}
          </AnimatedSection>

          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:items-start md:justify-end">
            <Button asChild variant="outline" className="border-slate-200 bg-white shadow-sm">
              <Link to="/contact">Request a demo</Link>
            </Button>
          </div>
        </div>

        <div className="mt-6 grid gap-6 sm:grid-cols-2 md:gap-7 xl:grid-cols-4">
          {ordered.map((product, index) => (
            <CatalogCard
              key={product.slug || slugifyCatalogName(product.name) || `${product.name}-${index}`}
              item={product}
              index={index}
              theme={theme}
              idleLabel={idleLabel}
              kind={kind}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
